import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PrivacyControls = ({ user, userProfile, onSuccess }) => {
  const [settings, setSettings] = useState({
    profileVisibility: 'public',
    showEmail: false,
    showProgress: true,
    allowMessages: true
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const handleVisibilityChange = (value) => {
    setSettings(prev => ({ ...prev, profileVisibility: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    // Simulate save
    setTimeout(() => {
      setIsSaving(false);
      onSuccess?.('Privacy settings updated successfully!');
    }, 1000);
  };

  return (
    <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-heading font-bold text-white mb-2">
          Privacy Controls
        </h2>
        <p className="text-blue-100">
          Manage your privacy and data sharing preferences
        </p>
      </div>

      {/* Profile Visibility */}
      <div className="bg-white bg-opacity-5 rounded-xl p-6 space-y-4">
        <div className="flex items-center space-x-3">
          <Icon name="Eye" size={24} className="text-purple-400" />
          <div>
            <h3 className="text-lg font-semibold text-white">Profile Visibility</h3>
            <p className="text-blue-200 text-sm">Control who can see your profile</p>
          </div>
        </div>

        <div className="space-y-2">
          {['public', 'private', 'friends']?.map((option) => (
            <button
              key={option}
              onClick={() => handleVisibilityChange(option)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                settings?.profileVisibility === option
                  ? 'bg-purple-600 text-white' :'bg-white bg-opacity-10 text-blue-100 hover:bg-opacity-20'
              }`}
            >
              <span className="capitalize font-medium">{option}</span>
              {settings?.profileVisibility === option && (
                <Icon name="Check" size={20} />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Data Sharing */}
      <div className="bg-white bg-opacity-5 rounded-xl p-6 space-y-4">
        <div className="flex items-center space-x-3">
          <Icon name="Share2" size={24} className="text-blue-400" />
          <div>
            <h3 className="text-lg font-semibold text-white">Data Sharing</h3>
            <p className="text-blue-200 text-sm">Choose what information to share</p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between py-3 border-b border-white border-opacity-10">
            <div>
              <p className="text-white font-medium">Show Email Address</p>
              <p className="text-blue-200 text-sm">Display email on your profile</p>
            </div>
            <button
              onClick={() => handleToggle('showEmail')}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                settings?.showEmail ? 'bg-purple-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  settings?.showEmail ? 'translate-x-7' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-white border-opacity-10">
            <div>
              <p className="text-white font-medium">Show Progress</p>
              <p className="text-blue-200 text-sm">Display learning progress publicly</p>
            </div>
            <button
              onClick={() => handleToggle('showProgress')}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                settings?.showProgress ? 'bg-purple-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  settings?.showProgress ? 'translate-x-7' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <p className="text-white font-medium">Allow Messages</p>
              <p className="text-blue-200 text-sm">Let others send you messages</p>
            </div>
            <button
              onClick={() => handleToggle('allowMessages')}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                settings?.allowMessages ? 'bg-purple-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  settings?.allowMessages ? 'translate-x-7' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          variant="primary"
          disabled={isSaving}
          iconName="Save"
          iconPosition="left"
          className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

export default PrivacyControls;